/**
 * F 453 — WHICH DATABASE, IF ANY, DOES THIS URL ADDRESS?
 *
 * Extracted as a pure function so the decision is testable without mounting
 * the whole workspace provider tree — and, more importantly, so the predicate
 * is readable in one place. F 451's lesson was that a routing bug can hide
 * behind an attribute that looks right; this function is the thing that
 * decides, and it can be asked directly.
 *
 * Returns the database id for `/database/:databaseId`, else null. `null` means
 * "not a routed database", which routes the main area back to its pre-existing
 * behaviour rather than to an empty database surface.
 *
 * QUERY AND HASH ARE TOLERATED, and the regex has to say so explicitly: the
 * id class is `[^/?#]+` so it cannot swallow a `?`, but without an explicit
 * `(?:\?[^#]*)?(?:#.*)?$` tail the `$` anchor then refuses the whole match and
 * a bookmarked `/database/<id>?x=1` returns null. Caught by its own test.
 */
export function databaseIdFromPath(path: string): string | null {
  const m = /^\/database\/([^/?#]+)\/?(?:\?[^#]*)?(?:#.*)?$/.exec(path || '');
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Workspace Surface Component
 * Phase 7: Workspace Surface Layer - Main Container
 * 
 * Primary surface that combines:
 * - PageTree sidebar for navigation
 * - ActiveWorkspace content area
 * - SurfaceLayout for responsive layout
 * 
 * This is the main workspace interface that users interact with.
 */

import React, { useEffect, useState } from 'react';
import SurfaceLayout from '../common/SurfaceLayout';
import { SidebarV2 } from '../common/SidebarV2';
import { ActiveWorkspace } from './ActiveWorkspace';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';
import { useRouteSync } from '../../../core/router/useRouteSync';
import { useMantineColorScheme } from '@mantine/core';
import { Box, Stack, Group, ActionIcon, Tooltip } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconMenu2 } from '@tabler/icons-react';
import { eventBus } from '../../../core/event-bus';
import { SidebarCreateMenu } from '../common/SidebarCreateMenu';
import type { TaskViewStatus } from '../../../types/models/Task';
// F 78 — install the global Cmd+T / Cmd+Shift+T handler + mount QuickCaptureModal
// so the "New task" sidebar button (which dispatches `fc:quick-capture:toggle`)
// and the keyboard shortcut both open the same modal. Without this hook + modal
// mount, neither the button nor the shortcut had a listener (F 77 wired the
// dispatcher but the listener was orphan).
import { useQuickCapture } from '../../../core/task/use-quick-capture';
import { QuickCaptureModal } from '../task/QuickCaptureModal';
// F 468 - `/database/:id` renders ANY database, not only HQ Backup.
//
// F 473 - AND IT GETS THE TOOLBAR. Measured on production (build `a5f2b6f`):
// the routed database had NO toolbar, NO tabs, NO search and NO filters - and
// it had none on DESKTOP either, because F 468's `GeneralDatabaseView` is a
// read-only card grid. The component that owns the whole toolset is
// `DatabaseView` (tab bar, CRUD dropdown, search, FilterRow, and its own
// `isMobile` branch), so the routed path reaches THAT instead. Both read the
// same key (`freshcards:databases`), so the DATA is unchanged - measured:
// both see Quick Notes' 641 items.
//
// F 497 - AND HQ BACKUP GETS IT TOO. It used to be routed to `HqDatabaseView`
// - a separate surface with its own stylesheet, its own card and badge and NO
// toolbar at all - which is why the operator filed the dispatch. It is no
// longer imported here: the route reaches `RoutedDatabaseView` for EVERY
// database, and `DatabaseView` reads a PAGED database's first page from the
// graph. `HqDatabaseView` itself is untouched and still exported.
import { RoutedDatabaseView } from './RoutedDatabaseView';
import { getSurfaceRouter } from '../../../core/router/SurfaceRouter';
import { TasksView } from '../task/TasksView';
// F 226 — TRASH
import { TrashView } from './TrashView';

/**
 * Workspace Surface Props
 */
interface WorkspaceSurfaceProps {
  /** Phase number */
  phase?: number;
  /** Custom className */
  className?: string;
  /** Custom style */
  style?: React.CSSProperties;
  /** Enable/disable sidebar toggle on mobile */
  responsiveSidebarToggle?: boolean;
  /** Force specific layout (overrides responsive) */
  forceLayout?: 'mobile' | 'tablet' | 'desktop' | 'wide';
}

/**
 * Workspace Sidebar Header Component
 */
const WorkspaceSidebarHeader: React.FC<{
  collapsed?: boolean;
  onCollapse?: () => void;
}> = ({ collapsed = false, onCollapse }) => {
  return (
    <Box
      p="md"
      style={{
        borderBottom: '1px solid var(--mantine-color-gray-2)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
        {!collapsed && 'Workspace'}
      </h2>
      {/* F 472 — the global "+" sits beside the collapse control, where the
          eye goes first. It asks what to make: page, database or task. */}
      <Group gap={2} wrap="nowrap">
        <SidebarCreateMenu />
        {onCollapse && (
          <Tooltip label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={onCollapse}
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              {collapsed ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    </Box>
  );
};

/**
 * Workspace Sidebar Component
 *
 * F 194: mounts SidebarV2 (task nav + the page tree) instead of a bare
 * PageTree. SidebarV2 imports and mounts PageTree itself, so the page tree
 * is still the same component — this is the build step of F 191's option B
 * (build the merged sidebar beside PageTree, delete later).
 */
const WorkspaceSidebar: React.FC<{
  phase: number;
  currentPageId?: string;
  // F 451 — `kind` was dropped HERE, not in SidebarV2.
  //
  // `handlePageSelect` (line 174) accepts `(pageId, kind?)` and passes `kind`
  // to `navigateToPage`, which is what routes a database row to DatabaseView.
  // But this prop was typed `(pageId: string) => void`, so the second argument
  // never arrived: the call succeeded, the URL changed, and the main pane kept
  // rendering Today. The click was wired and the routing was not.
  //
  // Widening the type is the whole fix — SidebarV2 already forwards `kind`, and
  // PageTree rows pass one argument, which stays valid.
  onPageSelect?: (pageId: string, kind?: string) => void;
  activeTaskView?: TaskViewStatus | null;
  onTaskNavigate?: (view: TaskViewStatus) => void;
  collapsed?: boolean;
  onCollapse?: () => void;
  /** F 226 — TRASH. */
  trashOpen?: boolean;
  onTrashToggle?: () => void;
}> = ({
  phase,
  currentPageId,
  onPageSelect,
  activeTaskView,
  onTaskNavigate,
  collapsed = false,
  onCollapse,
  trashOpen = false,
  onTrashToggle
}) => {
  return (
    <Stack gap={0} h="100%">
      <WorkspaceSidebarHeader collapsed={collapsed} onCollapse={onCollapse} />
      <Box
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        <SidebarV2
          phase={phase}
          currentPageId={currentPageId}
          onPageSelect={onPageSelect}
          activeTaskView={activeTaskView}
          onTaskNavigate={onTaskNavigate}
          trashOpen={trashOpen}
          onTrashToggle={onTrashToggle}
        />
      </Box>
    </Stack>
  );
};

/**
 * Workspace Surface Component
 * 
 * Main container for the entire workspace interface.
 * Manages layout, sidebar visibility, and content routing.
 */
export const WorkspaceSurface: React.FC<WorkspaceSurfaceProps> = ({
  phase = 7,
  className = '',
  style = {},
  responsiveSidebarToggle = true,
  forceLayout
}) => {
  const { currentPageId, selectPage, sidebarCollapsed, toggleSidebar } = useWorkspace();
  const { navigateToPage } = useRouteSync();
  const { colorScheme } = useMantineColorScheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // F 78 — install the global Cmd+T / Cmd+Shift+T handler + capture `opened`
  // state. Returning `{ opened, close }` because we only need to react to the
  // hook's toggle events (the sidebar button dispatches `fc:quick-capture:toggle`
  // which useQuickCapture already listens for at line 67 of use-quick-capture.ts).
  const { opened: quickCaptureOpened, close: closeQuickCapture } = useQuickCapture();

  // F 194 — which task view (if any) the operator has selected in SidebarV2.
  // null means "a page is in focus". The main area renders TasksView for it.
  const [activeTaskView, setActiveTaskView] = useState<TaskViewStatus | null>(null);

  // Handle page selection from sidebar - now with URL sync
  //
  // F 320 — `kind` is forwarded, not decided here. The sidebar is the only site
  // that knows a row is a database (it maps `dbPages`, SidebarV2.tsx:98). This
  // handler and navigateToPage receive an id and nothing else, so the kind has to
  // arrive with it. Omitting `kind` keeps the page path unchanged.
// F 453 — THE ROUTE CONSUMER. This is the piece F 451 measured as missing.
  //
  // `App.tsx` renders `<WorkspaceSurface />` unconditionally and never reads the
  // URL, so before this, `/database/<id>` changed in the address bar and the
  // main area kept rendering whatever `activeTaskView`/`trashOpen` said. A URL
  // is a REQUEST to render; something has to read it. This is that something.
  //
  // Two triggers, deliberately separate:
  //   - mount, for a pasted/bookmarked URL;
  //   - `popstate`, for back/forward.
  // `navigateToPage` uses `history.replaceState`, which does NOT fire popstate,
  // so the click path is covered by this state directly (handlePageSelect sets
  // it) rather than by listening for history events.
  //
  // Declared ABOVE `handlePageSelect` on purpose: that handler assigns it, and
  // a state hook read by an earlier-defined closure reads as a temporal dead
  // zone even though the call happens later. Order it correctly instead.
  const [dbRoute, setDbRoute] = useState<string | null>(null);

  useEffect(() => {
    const read = () => {
      // The router's own resolution is authoritative for "is this a surface
      // route", but the pure predicate decides the PARAM, so this path cannot
      // silently disagree with what the sidebar wrote into the URL.
      const r = getSurfaceRouter().getCurrentRoute();
      const fromRouter = r && r.isValid && r.surface === 'DatabaseView'
        ? r.params.databaseId ?? null
        : null;
      setDbRoute(fromRouter ?? databaseIdFromPath(window.location.pathname));
    };
    read();
    window.addEventListener('popstate', read);
    return () => window.removeEventListener('popstate', read);
  }, []);

  const handlePageSelect = async (pageId: string, kind?: string) => {
    // Selecting a page clears any active task view — the two are exclusive.
    setActiveTaskView(null);
    // F 453 — a database selection is a ROUTE change, so leave the routed
    // database; a page selection is the opposite, so enter the routed content.
    // Without this the previous database would stay on screen underneath the
    // newly selected page, because `navigateToPage` uses replaceState and
    // therefore never fires the popstate listener above.
    setDbRoute(kind === 'database' ? pageId : null);
    await selectPage(pageId);
    navigateToPage(pageId, kind);
  };

  // Handle sidebar toggle via event bus (from other components)
  useEffect(() => {
    const handler = () => toggleSidebar();
    eventBus.on('fc:sidebar:toggle', handler);
    return () => eventBus.off('fc:sidebar:toggle', handler);
  }, [toggleSidebar]);

  // F 226 — TRASH. Local to the workspace: the Trash view takes over the
  // main area the same way a task view does, and neither owns the other.
  const [trashOpen, setTrashOpen] = useState(false);
  // Bumped after a soft delete so an open Trash re-reads. TrashView
  // memoises its read on this key rather than owning the delete event.
  const [trashRefresh, setTrashRefresh] = useState(0);

  useEffect(() => {
    const onDeleted = () => setTrashRefresh((n) => n + 1);
    eventBus.on('page-deleted', onDeleted);
    return () => eventBus.off('page-deleted', onDeleted);
  }, []);

  // Render sidebar content
  const sidebarContent = (
    <WorkspaceSidebar
      phase={phase}
      currentPageId={currentPageId}
      onPageSelect={handlePageSelect}
      activeTaskView={activeTaskView}
      onTaskNavigate={setActiveTaskView}
      collapsed={sidebarCollapsed}
      onCollapse={toggleSidebar}
      trashOpen={trashOpen}
      onTrashToggle={() => setTrashOpen((v) => !v)}
    />
  );

  // F 453 — the route consumer lives above `handlePageSelect`; see there.

  // Render main content. A selected task view takes over the main area; with
  // none selected we fall through to the existing ActiveWorkspace behaviour.
  //
  // F 453 — the database branch sits ABOVE the task view on purpose. `trashOpen`
  // and `activeTaskView` are in-app modes with no URL of their own; a routed
  // database IS addressed by the URL, so arriving at `/database/x` must win,
  // or a stale task view would swallow every bookmarked database link.
  const mainContent = (
    <Box h="100%" w="100%" style={{ display: 'flex', flexDirection: 'column' }}>
      {dbRoute ? (
        /**
         * F 497 — EVERY DATABASE GETS THE SAME SURFACE.
         *
         * MEASURED, and this is the whole reason the operator filed the
         * dispatch: HQ Backup was rendered by `HqDatabaseView`, a SEPARATE
         * surface with its own stylesheet, its own card and badge, its own
         * header and NO TOOLBAR — so the routed database had no Gallery /
         * Board / List tabs, no search and no filters, and its sidebar
         * badge read "HQ Backup 0" while the page itself said 16,783.
         *
         * ⇒ THE NORMAL SURFACE IS THE ONE THAT OWNS THE TOOLBAR. `DatabaseView`
         *   has the view switcher, the CRUD menu, the search field, the
         *   FilterRow and the app's own card and theme (F 473 measured that
         *   `GeneralDatabaseView` has NONE of these — it is a read-only
         *   card grid — so the routed path reaches `DatabaseView`, and that
         *   is what every other database already gets).
         *
         * ⇒ THE DATA IS THE SAME KEY. Both read `freshcards:databases`, and
         *   `DatabaseView` now reads a PAGED database's first page from the
         *   graph (F 497), so HQ Backup's cards load in the normal surface.
         *
         * ⇒ `HqDatabaseView` IS NOT DELETED. It stays in the tree and in
         *   `ui/faces.ts` — it is the surface that knows how to read the
         *   whole 16,783-item backup at once, and F 453/F 486's specs pin
         *   it. What changed is that the ROUTE no longer reaches for it.
         */
        <RoutedDatabaseView databaseId={dbRoute} />
      ) : trashOpen ? (
        <TrashView refreshKey={trashRefresh} />
      ) : activeTaskView ? (
        <TasksView status={activeTaskView} />
      ) : (
        <ActiveWorkspace />
      )}
    </Box>
  );

  return (
    <Box
      className={`workspace-surface ${className}`}
      h="100%"
      w="100%"
      style={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--fc-bg-primary)',
        ...style
      }}
    >
      <SurfaceLayout
        sidebar={sidebarContent}
        main={mainContent}
        responsive={true}
        forceLayout={forceLayout}
        debugMode={false}
      />
      {/* F 78 — mount the QuickCaptureModal so the global Cmd+T / Cmd+Shift+T
          handler AND the sidebar "New task" button (both routed through
          `fc:quick-capture:toggle`) can open it. Modal returns null when
          `opened` is false, so the cost is one React node. */}
      <QuickCaptureModal
        opened={quickCaptureOpened}
        onClose={closeQuickCapture}
      />
    </Box>
  );
};

/**
 * Export component and context provider hook
 */
export { WorkspaceProvider, useWorkspace } from '../../../core/workspace/WorkspaceContext';
export type { WorkspaceSurfaceProps };

export default WorkspaceSurface;